//@ts-check
import React, { useContext, useEffect, useRef } from 'react';
import { pickBy, find } from 'lodash-es';
import { itemIsAccessory } from '../../../../utils/accessories';
import { getVariantsBySelections, getProductsDataByColorSelection } from '../product-utils';
import { IPdpData, TUserSelections, IProduct } from '../../types/PDP';
import { useCartData } from '../../../../utils/cart';
import { ALO_MOVES_REAL_SKU, E_GIFT_CARD_PRODUCT_TYPE, MUI_MOBILE_BREAKPOINT } from '../../../../utils/constants';
import { useElementDimensions, useIntersectionObserver } from '../../../_shared/react-hooks';
import {
  computeProductOptionLabel,
  computeUnselectedOptionName,
  scrollToSizeSelector,
  scrollToNotifyField,
  scrollToSimilarItems,
  scrollToColorSwatches,
  isEligibleForPreorder,
  hasAdvanceOrderAndPreorderTags,
} from '../../utils';
import { ProductContext } from '../../ProductContext';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Price from '../Price';
import './stickyATCstyle.scss';
import { checkIfProductIsPhasedOut } from '../VariantSelections/utils';
import FormInput from 'src/scripts/templates/_shared/ui-components/YogaUI/FormInput';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import Checkbox from 'src/scripts/templates/_shared/ui-components/YogaUI/Checkbox';
import { ShippingCountryState } from 'src/scripts/utils/shipping';
import { checkIfItemCanBeSoldOut, heapTrack } from 'src/scripts/utils/helpers';
import { ColorSelectionUI } from '../ReusableColorSwatchesContainer';
import {
  extractPurchaseLimitTagData,
  hasMetLimitedProductCartQuantity,
} from 'src/scripts/templates/_shared/ui-components/PurchaseLimitItem/PurchaseLimitMessaging';
import { notNil } from 'src/scripts/utils/typescriptUtils';
import { determineATCandWishlistEligibility, hasHiddenInventoryTag } from 'src/scripts/utils/plp-pdp-shared';
import NotifyMe from '../NotifyMe';
import ButtonWithProgress from 'src/scripts/templates/_shared/ui-components/YogaUI/ButtonWithProgress';

const AddToCart = (props: ATCprops) => {
  const {
    pdpData,
    userSelections,
    currentTags,
    testId,
    ATBtype = 'Standard',
    similarItemsCarouselExists,
    giftCardRecipientFormIncomplete,
  } = props;

  const {
    addToCart,
    purchaseInProgress,
    isSelectionError,
    toggleSelectionError,
    assignProductData,
    changeIsModalOpenByATCbutton,
  } = useContext(ProductContext);
  const selectedVariants = getVariantsBySelections(pdpData, userSelections);
  // Push data into context
  useEffect(() => {
    assignProductData({ pdpData, selectedVariants, currentTags, userSelections });
    return () => {};
    // ! [REVISIT]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSelections]);

  const hasVariantsSelected = selectedVariants.every((item) => item);
  const selectedProducts = getProductsDataByColorSelection(pdpData, userSelections);
  const isAtLeastOneSelectedWithNotify =
    pdpData.isASet &&
    selectedVariants.some((variant, index) => {
      const { tags: tagsList = [] } = selectedProducts?.[index] || {};
      return (
        variant &&
        !checkIfProductIsPhasedOut(tagsList || []) &&
        (!variant?.availableForSale || hasHiddenInventoryTag(tagsList))
      );
    });
  const isAtLeastOneSizeSelectedWithoutNotify =
    pdpData.isASet &&
    selectedVariants.some((variant, index) => {
      const { tags: tagsList } = selectedProducts?.[index] || {};
      return (
        variant &&
        checkIfProductIsPhasedOut(tagsList || []) &&
        (!variant?.availableForSale || hasHiddenInventoryTag(tagsList))
      );
    });

  const hasValidVariantSelected = selectedVariants.every((item) => item && item.availableForSale);
  const shouldHideInventory = hasHiddenInventoryTag(currentTags);
  const nonSetProductColorHasValidSizeOptions = !!find(pdpData.products[0].variants, {
    selectionOptions: pickBy(userSelections[0]),
    availableForSale: true,
  });

  const estimatedShipDate = selectedVariants[0]?.estimatedShipDate?.value ?? null;
  const isAdvanceOrder = hasAdvanceOrderAndPreorderTags(currentTags) && !selectedVariants[0];
  const shouldShowPreorder =
    isAdvanceOrder ||
    isEligibleForPreorder(selectedVariants[0]?.quantityAvailable ?? 0, currentTags, estimatedShipDate);

  const optionNameDisplay = computeAtcDisplayOptionName(userSelections, pdpData.products);
  const isProductPhasedOut = checkIfProductIsPhasedOut(currentTags);
  const shouldShowNotifyMe = !isProductPhasedOut && (!selectedVariants[0]?.availableForSale || shouldHideInventory);
  const SoldOutCopy = () => {
    return isProductPhasedOut ? (
      <>Sold Out Online</>
    ) : (
      <>
        Notify Me <span className={`sticky-atc-hidden`}>Once in Stock</span>
      </>
    );
  };
  useEffect(() => {
    if (!nonSetProductColorHasValidSizeOptions) {
      heapTrack('PDP - View - Sold Out Product', { 'Product Phased Out': isProductPhasedOut });
    }
  }, [nonSetProductColorHasValidSizeOptions, isProductPhasedOut]);

  // AloMoves
  // [REVISIT] Should we still have it with a loyalty in place???
  const cartData = useCartData();
  const hasRealAloMovesInCart = cartData.regularItems.some((item) => item.sku === ALO_MOVES_REAL_SKU);
  // Note that technically if there was a set where one of the items in a Product WAS AM and one was not, the next line would false-positive
  // That would be insane though, so I don't care. This will perfectly handle the more likely use case of us making a Set of Alo Moves plus
  // physical goods! That kind of set mustn't be put in your cart if you already have one Real Alo Moves in it.
  const thisIsRealAloMovesBeingAdded = pdpData.products
    .flatMap((p) => p.variants.map((v) => v.sku))
    .some((sk) => sk === ALO_MOVES_REAL_SKU);

  const userAgent = navigator.userAgent.toLowerCase();
  if (thisIsRealAloMovesBeingAdded && userAgent.indexOf('followapp') > -1) {
    return <p>This app does not support subscription purchases. Please visit our website to add to cart.</p>;
  }

  const { totalInventory = 0 } = selectedProducts?.[0] || {};
  const isSoldOut = checkIfItemCanBeSoldOut(currentTags) ? shouldHideInventory || totalInventory <= 0 : false;
  const purchaseLimitItem = extractPurchaseLimitTagData(currentTags);
  const hasMetATCLimit =
    purchaseLimitItem && hasMetLimitedProductCartQuantity(cartData, selectedVariants[0]?.sku, purchaseLimitItem);

  const aloMovesAlreadyInCart = hasRealAloMovesInCart && thisIsRealAloMovesBeingAdded;
  const addToCartDisabled =
    (pdpData.isASet
      ? !hasValidVariantSelected && (hasVariantsSelected || isSelectionError || isAtLeastOneSelectedWithNotify)
      : !nonSetProductColorHasValidSizeOptions && isProductPhasedOut) ||
    aloMovesAlreadyInCart ||
    hasMetATCLimit ||
    (isProductPhasedOut && shouldHideInventory) ||
    giftCardRecipientFormIncomplete;

  const showSimilarItemsButton = pdpData.isASet
    ? similarItemsCarouselExists &&
      !hasValidVariantSelected &&
      hasVariantsSelected &&
      isAtLeastOneSizeSelectedWithoutNotify
    : (isProductPhasedOut &&
        !shouldShowNotifyMe &&
        similarItemsCarouselExists &&
        !selectedVariants[0]?.availableForSale &&
        userSelections[0].Size) ||
      (userSelections[0].Size && !selectedVariants[0]) ||
      (similarItemsCarouselExists && isSoldOut && isProductPhasedOut);

  const buttonCTA = shouldShowPreorder ? 'Preorder' : 'Add to Bag';

  return showSimilarItemsButton ? (
    <button type="button" className="product__buy--button button-primary" onClick={scrollToSimilarItems}>
      See Similar Items
    </button>
  ) : (
    <ButtonWithProgress
      options={{
        showProgressAnimation: purchaseInProgress,
        testId: testId,
        type: 'button',
        disabled: addToCartDisabled,
        buttonTypeClass: ATBtype === 'Sticky' ? 'button-invert' : 'button-primary',
        buttonClasses: `product__buy--button ${addToCartDisabled ? 'btn__disabled' : ''} ${
          purchaseInProgress ? 'product__buy--button--adding' : ''
        }`,
        cta: aloMovesAlreadyInCart ? (
          'Limit 1 Per Customer'
        ) : purchaseInProgress ? (
          'Adding'
        ) : shouldHideInventory ? (
          <SoldOutCopy />
        ) : hasValidVariantSelected ? (
          buttonCTA
        ) : pdpData.isASet ? (
          hasVariantsSelected ? (
            `Select Available Options`
          ) : // ? [REVISIT] is it always correct to add the `s` here?
          isSelectionError ? (
            `Select ${optionNameDisplay}s`
          ) : isAtLeastOneSelectedWithNotify ? (
            `Select Available Options`
          ) : (
            buttonCTA
          )
        ) : nonSetProductColorHasValidSizeOptions ? (
          buttonCTA
        ) : (
          <SoldOutCopy />
        ),
        onClick: async () => {
          if (shouldShowNotifyMe && userSelections[0].Size && ATBtype === 'Sticky') {
            scrollToNotifyField();
            return;
          }
          if (hasValidVariantSelected && !shouldHideInventory) {
            if (pdpData.isASet) {
              toggleSelectionError(false);
            }
            await addToCart(ATBtype);
          } else {
            if (!pdpData.isASet) {
              if (nonSetProductColorHasValidSizeOptions && !shouldHideInventory) {
                changeIsModalOpenByATCbutton(true);
              }
            }
            toggleSelectionError(true);
            if (ATBtype === 'Sticky') {
              scrollToSizeSelector();
            }
          }
        },
      }}
    />
  );
};

const AddToCartWrapper = (props: ATCprops) => {
  const { pdpData, userSelections } = props;
  const selectedProducts = getProductsDataByColorSelection(pdpData, userSelections);
  const flattenTags = Array.from(new Set(selectedProducts.flatMap((product) => product?.tags).filter(notNil)));
  const cantBeAddedToCartBecauseOfRestrictions = determineATCandWishlistEligibility(flattenTags);
  const { country } = ShippingCountryState;
  return cantBeAddedToCartBecauseOfRestrictions ? (
    <UnavailableAddToCartButton
      localizedCountryName={country?.name ?? 'Your Region'}
      countryISOCode={country?.code ?? ''}
      productHandle={pdpData.pdpProductShell.handle}
    />
  ) : (
    <AddToCartButtonUI {...props} />
  );
};

export default AddToCartWrapper;

const isStickyNavbar = () => {
  const navBar = document.querySelector('#shopify-section-main-nav');
  return navBar && getComputedStyle(navBar).position === 'sticky';
};

const AddToCartButtonUI = (props: ATCprops) => {
  const { similarItemsCarouselExists, giftCardRecipientType, areGiftCardRecipientFieldsValid } = useContext(
    ProductContext
  );
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });
  const mainATCbutton = useRef<HTMLDivElement>(null);
  const stickyBlock = useRef<HTMLDivElement>(null);
  const { isIntersecting: onScreen } = useIntersectionObserver(mainATCbutton);
  const boundingClientRect =
    mainATCbutton.current?.getBoundingClientRect && mainATCbutton.current.getBoundingClientRect();
  const isMainATCbuttonOnScreenOrBelowFold = boundingClientRect && boundingClientRect.top > 0 ? true : onScreen;

  const {
    productInfoForStickyButton: { variantImage, title, isFinalSale, displayPrice, actualPrice },
    userSelections,
    pdpData,
    currentTags,
  } = props;
  const { isASet, products } = pdpData;
  const { Color: selectedColor, Size: selectedSize } = userSelections[0];
  const landedProduct = pdpData.products[0];
  const swatchSections = landedProduct.swatchUiDataByColor[selectedColor ?? '']?.sections ?? [];

  const { height: navHeight } = useElementDimensions('#shopify-section-main-nav');
  const { height: universalBannerHeight } = useElementDimensions('.sticky-uni-banner');
  const stickyAtcTop = isStickyNavbar() ? universalBannerHeight + navHeight : universalBannerHeight;

  const purchaseLimitItem = extractPurchaseLimitTagData(pdpData?.pdpProductShell.tags);
  const giftCardRecipientFormIncomplete =
    landedProduct.productType.toLowerCase() === E_GIFT_CARD_PRODUCT_TYPE &&
    giftCardRecipientType === 'someoneElse' &&
    !areGiftCardRecipientFieldsValid;

  const shouldRenderStickyATCbutton =
    !isASet && !purchaseLimitItem && !isMainATCbuttonOnScreenOrBelowFold && !giftCardRecipientFormIncomplete;

  const isProductPhasedOut = checkIfProductIsPhasedOut(props.currentTags);
  const selectedVariants = getVariantsBySelections(pdpData, userSelections);
  const shouldHideInventory = hasHiddenInventoryTag(props.currentTags);
  const shouldShowNotifyMe = !isProductPhasedOut && (!selectedVariants[0]?.availableForSale || shouldHideInventory);

  // Appearance animation
  useEffect(() => {
    if (shouldRenderStickyATCbutton && stickyBlock.current) {
      stickyBlock.current.style.transform = 'translateY(0)';
    }
    return () => {};
  }, [shouldRenderStickyATCbutton]);

  let itemIsProbablyAnAccessory: boolean = itemIsAccessory(pdpData);

  return (
    <>
      <div ref={mainATCbutton}>
        {!isASet && shouldShowNotifyMe && selectedVariants[0] ? (
          <div className="notify-enable notify-form-container-0">
            <NotifyMe
              customerEmail={pdpData.customer.email}
              product={pdpData.products[0]}
              userSelection={userSelections[0]}
              variant={selectedVariants[0]}
              itemIsProbablyAnAccessory={itemIsProbablyAnAccessory}
              uniqueIDforCheckbox={'notify-enable-checkbox'}
              shouldShowNotifyMe={true}
              tags={currentTags}
            />
          </div>
        ) : (
          <AddToCart
            {...props}
            testId="add-to-cart-buy-button"
            similarItemsCarouselExists={similarItemsCarouselExists}
            giftCardRecipientFormIncomplete={giftCardRecipientFormIncomplete}
          />
        )}
      </div>
      <div
        ref={stickyBlock}
        style={{ top: stickyAtcTop + 'px' }}
        className={`sticky-atc-block ${shouldRenderStickyATCbutton ? 'd-flex' : 'd-none'}`}
      >
        <div className="product-info">
          {variantImage ? <img src={variantImage} alt="" className="img-fluid" /> : null}
          <div className="d-flex flex-column">
            <h3 className="product-title">{title}</h3>
            <Price
              actualPrice={actualPrice}
              displayPrice={displayPrice}
              isFinalSale={isFinalSale}
              withReviews={false}
              product={pdpData.products[0]}
            />
            {ShippingCountryState.country && ShippingCountryState.country?.dutiesCopy ? (
              <p className="small-p">({ShippingCountryState.country?.dutiesCopy})</p>
            ) : null}
          </div>
        </div>
        <div className={`actions`}>
          {!isMobile && selectedColor ? (
            <div className="variant__swatches">
              <div
                className="variant__swatches-color"
                style={{ marginRight: `${selectedSize ? '30px' : '10px'}` }}
                onClick={scrollToColorSwatches}
              >
                <p className="option-name swatch-label">
                  Color: <span>{selectedColor}</span>
                </p>
                <ColorSelectionUI
                  colorValue={selectedColor}
                  swatchSections={swatchSections}
                  isASet={isASet}
                  showBadge={false}
                  productDataByColorName={products[0].productDataByColorName}
                />
              </div>
              {selectedSize && (
                <p style={{ marginRight: 30 }} className="option-name">
                  Size: <span>{selectedSize}</span>
                </p>
              )}
            </div>
          ) : null}
          <AddToCart
            {...props}
            testId="add-to-cart-buy-button-sticky-with-info"
            similarItemsCarouselExists={similarItemsCarouselExists}
            ATBtype="Sticky"
          />
        </div>
      </div>
    </>
  );
};

// ? [REVISIT] copy?
const UnavailableAddToCartButton = ({ countryISOCode, localizedCountryName, productHandle }: UnavailableATCProps) => {
  const [email, setEmail] = React.useState('');
  const [aloNewsAndUpdates, setAloNewsAndUpdates] = React.useState(true);

  const onFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      const signupFormResponse = await signupFormSubmit({
        email,
        aloNewsAndUpdates,
        countryISOCode,
        productHandle,
      });

      if (signupFormResponse.ok) {
        await signupFormResponse.json();
        alert('Thanks! We will notify you when this product becomes available!');
      } else {
        throw new Error(`Couldn't get OK response from server`);
      }
    } catch (error) {
      alert('Sorry, something went wrong. Verify your email address?');
      console.warn('Something went wrong.', error);
    }
  };

  return (
    <>
      <div className="unavailable-item">
        <p className="form-heading">Item Currently Unavailable in Your Region</p>
        <p className="form-body">
          Due to international restrictions, this item is unavailable to ship to you at this time. Enter your email & we
          will let you know if it becomes available.{' '}
          <a href="/pages/beauty-faq#orders" target="_blank" rel="noopener">
            <u>Learn More</u>
          </a>
        </p>
        <form onSubmit={(event) => onFormSubmit(event)}>
          <div className="signup-form">
            <FormInput
              options={{
                id: 'singupEmail',
                name: 'singupEmail',
                label: 'Enter your email',
                type: 'email',
                required: true,
                wrapperClasses: 'form-body-input',
                onChange: (event) => setEmail(event.currentTarget.value),
                labelClasses: 'form-body-input-label',
              }}
            />
            <button className="button-primary arrow-button" type="submit">
              <ArrowForwardIosIcon className="arrow-forward-ios-icon" />
            </button>
          </div>
          <Checkbox
            options={{
              id: 'signup-checkbox',
              name: 'singupForAloNews',
              label: 'Sign up for Alo news & updates.',
              onChange: (event) => setAloNewsAndUpdates(event.target.checked),
              wrapperClasses: 'signup-checkbox',
              defaultChecked: true,
              checked: aloNewsAndUpdates,
            }}
          />
        </form>
      </div>

      <div className="unavailable-message">
        <p>Unavailable in {localizedCountryName}</p>
      </div>
    </>
  );
};

//  helper fuctions
const computeAtcDisplayOptionName = (userSelections: TUserSelections, products: IProduct[]) => {
  const selectionsWithProduct = userSelections.map((selection, index) => ({
    selection,
    // ? [REVISIT] make sure this `!` is safe; how do we express this in TS
    product: products[index]!,
  }));

  const firstUnselectedOption = selectionsWithProduct.find(({ selection }) => {
    const everyOptionIsSelected = Object.values(selection).every(Boolean);
    return !everyOptionIsSelected;
  });
  if (!firstUnselectedOption) return undefined;

  const { product, selection } = firstUnselectedOption;
  // ? [REVISIT] make sure this `!` is safe; how do we express this in TS
  const unselectedOptionName = computeUnselectedOptionName([selection])!;
  return computeProductOptionLabel({ optionName: unselectedOptionName, productType: product.productType });
};

const signupFormSubmit = async (signupFormDetails: SignupFormDetails) => {
  // tags need to include in email
  const tags = [
    'offer-banned-country-beauty',
    `offer-beauty-product-${signupFormDetails.productHandle}`,
    `offer-banned-country-beauty-${signupFormDetails.countryISOCode}`,
  ];

  const requestData = {
    accepts_marketing: signupFormDetails.aloNewsAndUpdates ? '1' : '0',
    email: signupFormDetails.email,
    tags,
  };

  return fetch(`https://${window?.wizUrl}/offer-enroll`, {
    method: 'POST',
    body: JSON.stringify(requestData),
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

interface ATCprops {
  pdpData: IPdpData;
  userSelections: TUserSelections;
  currentTags: string[];
  productInfoForStickyButton: productInfo;
  testId?: string;
  ATBtype?: string;
  similarItemsCarouselExists?: boolean;
  giftCardRecipientFormIncomplete: boolean;
}

interface productInfo {
  variantImage: string | undefined;
  actualPrice: number;
  displayPrice: number;
  isFinalSale: boolean;
  title: string | undefined;
}

interface UnavailableATCProps {
  localizedCountryName: string;
  countryISOCode: string;
  productHandle: string;
}

interface SignupFormDetails {
  email: string;
  aloNewsAndUpdates: boolean;
  countryISOCode: string;
  productHandle: string;
}

//in case we use it for something ever
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface OfferEnrollSuccessResponse {
  action: string;
  status: string;
  tagsAdded: string[];
}
